import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { UploadCloud, Download, CheckCircle, AlertCircle, X, Loader2 } from 'lucide-react';
import { fetchWithAuth } from '../utils/api';

export const BulkUploadModal = ({ isOpen, onClose, onSuccess, moduleName, apiBasePath, showNotification }) => {
  const [file, setFile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formatStatus, setFormatStatus] = useState(null); // { valid: boolean, message: string, errors: array }
  const [uploadResult, setUploadResult] = useState(null); // { summary, created, duplicates, errors }
  
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const resetState = () => {
    setFile(null);
    setFormatStatus(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetchWithAuth(`${apiBasePath}/download-sample-excel?t=${Date.now()}`);
      if (!res.ok) throw new Error('Failed to download template');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sample_${moduleName.toLowerCase()}_template_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      if (showNotification) showNotification('Error downloading template', 'error');
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const extension = selectedFile.name.split('.').pop().toLowerCase();
    if (extension !== 'xlsx' && extension !== 'xls') {
      setFormatStatus({ valid: false, message: 'Invalid file type. Please upload .xlsx or .xls', errors: [] });
      setFile(null);
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      setFormatStatus({ valid: false, message: 'File is too large. Maximum size is 5MB.', errors: [] });
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);
    setFormatStatus(null);
    setChecking(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const res = await fetchWithAuth(`${apiBasePath}/check-bulk-format`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        setFormatStatus(data);
      } else {
        setFormatStatus({ valid: false, message: 'Failed to verify format. Server error.', errors: [] });
      }
    } catch {
      setFormatStatus({ valid: false, message: 'Network error while checking file format.', errors: [] });
    } finally {
      setChecking(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !formatStatus?.valid) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetchWithAuth(`${apiBasePath}/bulk-upload`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        setUploadResult(data);
        if (onSuccess) onSuccess();
      } else {
        const err = await res.json();
        if (showNotification) showNotification(err.error || 'Upload failed', 'error');
      }
    } catch {
      if (showNotification) showNotification('Network error during upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h2 className="text-lg font-bold text-slate-800">Bulk Upload {moduleName}</h2>
          <button onClick={handleClose} className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          
          {/* Step 1: Template */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 text-sm">Step 1: Download Template</h3>
              <p className="text-xs text-blue-700 mt-1">Download the Excel template to see the required format.</p>
            </div>
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 text-xs font-bold rounded-lg transition-colors shadow-sm"
            >
              <Download size={14} /> Template
            </button>
          </div>

          {/* Step 2: Upload */}
          {!uploadResult && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800 text-sm">Step 2: Upload Excel File</h3>
              
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors
                  ${file ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5' : 'border-slate-300 hover:border-slate-400'}
                `}
              >
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                
                {checking ? (
                  <div className="flex flex-col items-center text-slate-500">
                    <Loader2 size={32} className="animate-spin mb-3 text-[var(--theme-primary)]" />
                    <p className="text-sm font-medium">Checking file format...</p>
                  </div>
                ) : file ? (
                  <div className="flex flex-col items-center">
                    <CheckCircle size={32} className="text-[var(--theme-primary)] mb-3" />
                    <p className="text-sm font-medium text-slate-800">{file.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                    <button 
                      onClick={() => fileInputRef.current.click()}
                      className="mt-4 text-xs font-semibold text-[var(--theme-primary)] hover:underline"
                    >
                      Change file
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <UploadCloud size={32} className="text-slate-400 mb-3" />
                    <p className="text-sm font-medium text-slate-800">Click to upload or drag and drop</p>
                    <p className="text-xs text-slate-500 mt-1">.xlsx or .xls up to 5MB</p>
                    <button 
                      onClick={() => fileInputRef.current.click()}
                      className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors shadow-sm"
                    >
                      Select File
                    </button>
                  </div>
                )}
              </div>

              {/* Format Check Result */}
              {formatStatus && (
                <div className={`p-4 rounded-xl border ${formatStatus.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {formatStatus.valid ? <CheckCircle size={16} className="text-green-600" /> : <AlertCircle size={16} className="text-red-600" />}
                    <h4 className={`text-sm font-bold ${formatStatus.valid ? 'text-green-800' : 'text-red-800'}`}>
                      {formatStatus.message}
                    </h4>
                  </div>
                  {!formatStatus.valid && formatStatus.errors && formatStatus.errors.length > 0 && (
                    <ul className="mt-2 pl-6 list-disc text-xs text-red-700 space-y-1">
                      {formatStatus.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  )}
                  {formatStatus.valid && formatStatus.dataRowCount !== undefined && (
                    <p className="mt-2 text-xs text-green-700 font-medium">Found {formatStatus.dataRowCount} valid rows to process.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Result Summary */}
          {uploadResult && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800 text-sm">Upload Summary</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{uploadResult.summary.totalCreated}</p>
                  <p className="text-xs font-bold uppercase tracking-wider text-green-800 mt-1">Created</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-amber-700">{uploadResult.summary.totalDuplicates}</p>
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mt-1">Duplicates</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">{uploadResult.summary.totalErrors}</p>
                  <p className="text-xs font-bold uppercase tracking-wider text-red-800 mt-1">Errors</p>
                </div>
              </div>

              {(uploadResult.duplicates?.length > 0 || uploadResult.errors?.length > 0) && (
                <div className="border border-slate-200 rounded-xl overflow-hidden max-h-[300px] flex flex-col">
                  <div className="overflow-auto custom-scrollbar">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-slate-50 sticky top-0 shadow-sm z-10">
                        <tr>
                          <th className="py-2.5 px-4 font-bold text-slate-600 border-b border-slate-200 w-16">Row</th>
                          <th className="py-2.5 px-4 font-bold text-slate-600 border-b border-slate-200 w-24">Status</th>
                          <th className="py-2.5 px-4 font-bold text-slate-600 border-b border-slate-200">Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {uploadResult.errors?.map((err, i) => (
                          <tr key={`err-${i}`} className="bg-red-50/50 hover:bg-red-50 transition-colors">
                            <td className="py-2.5 px-4 font-medium text-slate-800">{err.row}</td>
                            <td className="py-2.5 px-4 text-red-600 font-bold text-[10px] uppercase tracking-wider">Error</td>
                            <td className="py-2.5 px-4 text-slate-600 font-medium">{err.error}</td>
                          </tr>
                        ))}
                        {uploadResult.duplicates?.map((dup, i) => (
                          <tr key={`dup-${i}`} className="bg-amber-50/50 hover:bg-amber-50 transition-colors">
                            <td className="py-2.5 px-4 font-medium text-slate-800">{dup.row}</td>
                            <td className="py-2.5 px-4 text-amber-600 font-bold text-[10px] uppercase tracking-wider">Duplicate</td>
                            <td className="py-2.5 px-4 text-slate-600 font-medium">Already exists (ID: {dup.existingId})</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 shrink-0 flex items-center justify-end gap-3 bg-slate-50 rounded-b-2xl">
          <button
            onClick={handleClose}
            className="px-5 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition-colors"
          >
            {uploadResult ? 'Close' : 'Cancel'}
          </button>
          
          {!uploadResult && (
            <button
              onClick={handleUpload}
              disabled={!formatStatus?.valid || uploading}
              className="flex items-center gap-2 px-6 py-2.5 bg-[var(--theme-primary)] hover:bg-[var(--theme-primary-dark)] disabled:opacity-50 disabled:hover:bg-[var(--theme-primary)] text-white text-xs font-bold uppercase tracking-[0.05em] rounded-xl transition-colors shadow-lg"
            >
              {uploading ? (
                <><Loader2 size={16} className="animate-spin" /> Uploading...</>
              ) : (
                <><UploadCloud size={16} /> Start Upload</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

BulkUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  moduleName: PropTypes.string.isRequired,
  apiBasePath: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
};
